import { useEffect, useRef, useState } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import styles from './LawFirmTypeahead.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LawFirmTypeahead
 * Searches the law_firms collection by name and lets the user either pick a known firm
 * (capturing firm_id + canonical name) or type a free-text firm name (firm_id stays empty).
 *
 * CROSS-SURFACE: rendered by both /apply and ClientDashboard, so it owns its styles
 * rather than borrowing .mcl-form-control from a page stylesheet. Its module keeps the
 * pre-redesign appearance on purpose — see the header comment there before changing colours.
 *
 * Props:
 *  - value (string): current firm name displayed
 *  - firmId (string|null): currently selected firm_id (empty/null when manual)
 *  - onChange ({ firm_id, name }): called whenever the user picks a firm or types a new value
 *  - placeholder (string)
 *  - readOnly (boolean): when true, renders a read-only input (e.g., attorney auto-locked the firm)
 *  - hasError (boolean): red border styling
 *  - inputTestId (string): data-testid for the input
 *  - dropdownTestId (string): data-testid for the dropdown
 */
export const LawFirmTypeahead = ({
  value = '',
  firmId = '',
  onChange,
  placeholder = 'Search or type your law firm…',
  readOnly = false,
  hasError = false,
  inputTestId = 'input-law-firm-name',
  dropdownTestId = 'law-firm-dropdown'
}) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  // Keep local query in sync if the parent value changes externally (e.g., reset)
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Click-outside to close dropdown
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/law-firms/search?q=${encodeURIComponent(q.trim())}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.firms || []);
      }
    } catch (err) { console.error('Law firm search failed:', err); }
    finally { setLoading(false); }
  };

  const handleType = (next) => {
    setQuery(next);
    // Typing clears any previously linked firm_id (treat as manual entry)
    onChange?.({ firm_id: '', name: next });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(next), 250);
    if (next.trim().length >= 2) setOpen(true);
  };

  const selectFirm = (firm) => {
    setQuery(firm.name);
    setOpen(false);
    onChange?.({ firm_id: firm.firm_id, name: firm.name });
  };

  return (
    <div ref={wrapRef} className={styles.root}>
      <div className={styles.field}>
        <Search size={16} className={styles.searchIcon} aria-hidden="true" />
        <input
          className={`${styles.input}${hasError ? ` ${styles.inputError}` : ''}`}
          data-testid={inputTestId}
          value={query}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => { if (query.trim().length >= 2 && results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          readOnly={readOnly}
          autoComplete="off"
        />
        {loading && (
          <Loader2 size={16} className={`${styles.statusIcon} ${styles.spinner}`} aria-hidden="true" />
        )}
        {!loading && firmId && (
          <Check
            size={16}
            data-testid="law-firm-matched-icon"
            className={`${styles.statusIcon} ${styles.matched}`}
            aria-hidden="true"
          />
        )}
      </div>

      {open && !readOnly && results.length > 0 && (
        <div className={styles.dropdown} data-testid={dropdownTestId}>
          {results.map((firm) => (
            <button
              key={firm.firm_id}
              type="button"
              className={styles.option}
              onClick={() => selectFirm(firm)}
              data-testid={`law-firm-option-${firm.firm_id}`}
            >
              {firm.name}
            </button>
          ))}
          <div className={styles.hint}>
            Not in the list? Just keep typing your firm&apos;s name.
          </div>
        </div>
      )}
    </div>
  );
};

export default LawFirmTypeahead;
